import { c as _c } from "react/compiler-runtime";
import { useMutation } from '@apollo/client';
import { isNotEmpty } from '@newfront-insurance/core';
import { IconEyeOpen, Switch, useFuzzy } from '@newfront-insurance/core-ui';
import { Dialog, DialogContent, DialogHeader, DialogTitle, IconButtonAsAnchor, Input } from '@newfront-insurance/core-ui/v2';
import { graphql } from '@newfront-insurance/graphql-frontend';
import { useNotifications } from '@newfront-insurance/next-notifications';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { debounce } from 'lodash';
import type { JSX } from 'react';
import type { FeatureFlagControlPanelContextType, AppScopeEnum } from '../../../providers/feature-flag-control-panel';
export const TOGGLE_FEATURE_FLAG = graphql(`
  mutation ToggleFeatureFlag($input: ToggleFeatureFlagInput!) {
    toggleFeatureFlag(input: $input)
  }
`);
interface DevToolsModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  featureFlagControlPanelContext: FeatureFlagControlPanelContextType;
}
export function DevToolsModal(props) {
  const $ = _c(54);
  const {
    isOpen,
    onOpenChange,
    featureFlagControlPanelContext
  } = props;
  const {
    flagEvaluations,
    appScope
  } = featureFlagControlPanelContext;
  const ldClient = useLDClient();
  let t0;
  if ($[0] !== flagEvaluations || $[1] !== ldClient) {
    const allFlagEvaluations = ldClient?.allFlags();
    t0 = Array.from(flagEvaluations).map(flagKey => ({
      flagKey,
      evaluation: allFlagEvaluations?.[flagKey]
    }));
    $[0] = flagEvaluations;
    $[1] = ldClient;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const flagEvaluationsArray = t0;
  const notifications = useNotifications();
  let t1;
  if ($[3] !== notifications) {
    t1 = {
      onCompleted(_, someOtherData) {
        const {
          featureFlagId,
          variationValue
        } = someOtherData?.variables?.input ?? {};
        const title = isNotEmpty(featureFlagId) && isNotEmpty(variationValue) ? `${featureFlagId} set to ${variationValue}` : "Feature flag toggled";
        notifications.add({
          title,
          type: "success"
        });
      },
      onError(error) {
        notifications.add({
          title: error.message,
          type: "error"
        });
      }
    };
    $[3] = notifications;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const [toggleFeatureFlag, t2] = useMutation(TOGGLE_FEATURE_FLAG, t1);
  const {
    loading
  } = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      keys: ["flagKey"]
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    results: featureFlagSearchResults,
    search: onFeatureFlagSearchChange,
    query: featureFlagSearchQuery
  } = useFuzzy(flagEvaluationsArray, t3);
  let t4;
  if ($[6] !== onFeatureFlagSearchChange) {
    t4 = debounce(onFeatureFlagSearchChange, 500);
    $[6] = onFeatureFlagSearchChange;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const debouncedFeatureFlagSearch = t4;
  let T0;
  let T1;
  let t10;
  let t11;
  let t12;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[8] !== appScope || $[9] !== debouncedFeatureFlagSearch || $[10] !== featureFlagSearchQuery || $[11] !== featureFlagSearchResults || $[12] !== isOpen || $[13] !== loading || $[14] !== onOpenChange || $[15] !== toggleFeatureFlag) {
    const sortedFilteredFlagEvaluations = featureFlagSearchResults.sort(_temp);
    T1 = Dialog;
    t11 = isOpen;
    t12 = onOpenChange;
    T0 = DialogContent;
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <DialogHeader className="border-b pb-4"><DialogTitle>Newfront Dev Tools</DialogTitle></DialogHeader>;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    t7 = "flex flex-col gap-2 p-2";
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <p className="pb-2 text-base font-bold">Evaluated feature flags</p>;
      $[27] = t8;
    } else {
      t8 = $[27];
    }
    let t13;
    if ($[28] !== debouncedFeatureFlagSearch) {
      t13 = e => debouncedFeatureFlagSearch(e.target.value);
      $[28] = debouncedFeatureFlagSearch;
      $[29] = t13;
    } else {
      t13 = $[29];
    }
    if ($[30] !== featureFlagSearchQuery || $[31] !== t13) {
      t9 = <Input placeholder="Search feature flags" onChange={t13} type="search" defaultValue={featureFlagSearchQuery} />;
      $[30] = featureFlagSearchQuery;
      $[31] = t13;
      $[32] = t9;
    } else {
      t9 = $[32];
    }
    t5 = "flex h-[30vh] flex-col gap-2 overflow-y-auto";
    let t14;
    if ($[33] !== appScope || $[34] !== loading || $[35] !== toggleFeatureFlag) {
      t14 = flagEvaluation => <div key={flagEvaluation.flagKey} className="flex flex-row justify-between px-3 py-1"><div className="flex flex-row items-center gap-1"><span className="text-base">{flagEvaluation.flagKey}</span><IconButtonAsAnchor href={getFeatureFlagLaunchDarklyURL(flagEvaluation.flagKey, appScope)} target="_blank" rel="noreferrer" className="text-foreground"><IconEyeOpen color="currentColor" size={16} /></IconButtonAsAnchor></div>{typeof flagEvaluation.evaluation === "boolean" && <Switch enabled={flagEvaluation.evaluation} locked={loading} onClick={() => {
          toggleFeatureFlag({
            variables: {
              input: {
                featureFlagId: flagEvaluation.flagKey,
                variationValue: !flagEvaluation.evaluation,
                appScope
              }
            }
          });
        }} />}{typeof flagEvaluation.evaluation !== "boolean" && <span>Not supported</span>}</div>;
      $[33] = appScope;
      $[34] = loading;
      $[35] = toggleFeatureFlag;
      $[36] = t14;
    } else {
      t14 = $[36];
    }
    t6 = sortedFilteredFlagEvaluations.map(t14);
    $[8] = appScope;
    $[9] = debouncedFeatureFlagSearch;
    $[10] = featureFlagSearchQuery;
    $[11] = featureFlagSearchResults;
    $[12] = isOpen;
    $[13] = loading;
    $[14] = onOpenChange;
    $[15] = toggleFeatureFlag;
    $[16] = T0;
    $[17] = T1;
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    T0 = $[16];
    T1 = $[17];
    t10 = $[18];
    t11 = $[19];
    t12 = $[20];
    t5 = $[21];
    t6 = $[22];
    t7 = $[23];
    t8 = $[24];
    t9 = $[25];
  }
  let t13;
  if ($[37] !== t5 || $[38] !== t6) {
    t13 = <div className={t5}>{t6}</div>;
    $[37] = t5;
    $[38] = t6;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== t13 || $[41] !== t7 || $[42] !== t8 || $[43] !== t9) {
    t14 = <div className={t7}>{t8}{t9}{t13}</div>;
    $[40] = t13;
    $[41] = t7;
    $[42] = t8;
    $[43] = t9;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== T0 || $[46] !== t10 || $[47] !== t14) {
    t15 = <T0>{t10}{t14}</T0>;
    $[45] = T0;
    $[46] = t10;
    $[47] = t14;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  let t16;
  if ($[49] !== T1 || $[50] !== t11 || $[51] !== t12 || $[52] !== t15) {
    t16 = <T1 open={t11} onOpenChange={t12}>{t15}</T1>;
    $[49] = T1;
    $[50] = t11;
    $[51] = t12;
    $[52] = t15;
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  return t16;
}
function _temp(a, b) {
  return a.flagKey.localeCompare(b.flagKey);
}
function getFeatureFlagLaunchDarklyURL(flagKey: string, appScope: AppScopeEnum) {
  return `https://app.launchdarkly.com/projects/${appScope === 'ADMIN_NAVIGATOR' ? 'default' : 'client-dash'}/flags/${flagKey}`;
}